// CAMPAIGN MANAGEMENT WITH TOKEN-BASED AUTHENTICATION
// This is the corrected version with scalable token-based authentication
// All operations use authenticated shop_id from token (userId-username-role-mallId-shopId-timestamp)
// Author: MiniMax Agent

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '../types/auth';
import { MallApiService } from '../services/auth';
import QRGeneration from './QRGeneration';
import NotificationCenter from './NotificationCenter';
import WhatsAppTemplateManager from './WhatsAppTemplateManager';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

/**
 * Campaign Management Component
 * 
 * TOKEN-BASED AUTHENTICATION SYSTEM:
 * - Uses token format: userId-username-role-mallId-shopId-timestamp
 * - Example: "12-user-shop_admin-6-6-1763753837104"
 * - All operations use authenticated mall_id and shop_id from token
 * - Eliminates hardcoded usernames and complex permission arrays
 * 
 * Key Functions:
 * - parseAuthToken(): Extracts user data from token
 * - getCurrentUserMallAndShop(): Token-driven user identification
 * - All API calls use authenticated shop_id from token
 * - Debug logging throughout for tracking authentication
 */

// TypeScript interface for campaign templates
interface CampaignTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  visitorMessage: {
    title: string;
    body: string;
    emoji: string;
    theme: {
      primaryColor: string;
      secondaryColor: string;
      backgroundGradient: string;
      borderStyle: string;
      iconStyle: string;
    };
    actionButton: {
      text: string;
      color: string;
    };
  };
  offer: {
    discount: string;
    bonus: string;
    code: string;
    validUntil: string;
  };
  preview: {
    style: string;
    icon: string;
    color: string;
  };
  isDatabaseTemplate?: boolean;
}

type CampaignTemplateId = 'welcome-offer' | 'flash-sale' | 'gift-offer';

// CAMPAIGN TEMPLATES SYSTEM
const CAMPAIGN_TEMPLATES: Record<CampaignTemplateId, CampaignTemplate> = {
  'welcome-offer': {
    id: 'welcome-offer',
    name: 'Welcome Offer',
    description: 'Perfect for new customer onboarding',
    icon: '👋',
    visitorMessage: {
      title: '👋 Welcome to Our Store!',
      body: 'Welcome to our store! We\'re excited to have you here. As our guest, enjoy exclusive visitor pricing and special offers just for you.',
      emoji: '👋',
      theme: {
        primaryColor: '#3B82F6',
        secondaryColor: '#EFF6FF',
        backgroundGradient: 'from-blue-50 to-indigo-100',
        borderStyle: 'border-2 border-blue-200 rounded-xl',
        iconStyle: 'bg-blue-600 text-white'
      },
      actionButton: {
        text: 'Shop Now',
        color: 'bg-blue-600 hover:bg-blue-700'
      }
    },
    offer: {
      discount: '15% OFF',
      bonus: 'Welcome Gift',
      code: `WELC${Date.now().toString().slice(-4)}`,
      validUntil: 'Dec 21, 2025'
    },
    preview: {
      style: 'bg-blue-50 border-blue-200',
      icon: '👋',
      color: 'blue'
    }
  },
  'flash-sale': {
    id: 'flash-sale',
    name: 'Flash Sale',
    description: 'Time-limited promotional campaign',
    icon: '⚡',
    visitorMessage: {
      title: '⚡ Flash Sale Alert!',
      body: 'Don\'t miss out! We\'re offering exclusive deals for our visitors for a limited time only. Hurry, before the sale ends!',
      emoji: '⚡',
      theme: {
        primaryColor: '#EF4444',
        secondaryColor: '#FEF2F2',
        backgroundGradient: 'from-red-50 to-orange-100',
        borderStyle: 'border-2 border-red-200 rounded-xl',
        iconStyle: 'bg-red-600 text-white'
      },
      actionButton: {
        text: 'Shop Sale',
        color: 'bg-red-600 hover:bg-red-700'
      }
    },
    offer: {
      discount: '30% OFF',
      bonus: 'Limited Time',
      code: `FLASH${Date.now().toString().slice(-4)}`,
      validUntil: 'Today Only'
    },
    preview: {
      style: 'bg-red-50 border-red-200',
      icon: '⚡',
      color: 'red'
    }
  },
  'gift-offer': {
    id: 'gift-offer',
    name: 'Gift Offer',
    description: 'Special gifts for visitors',
    icon: '🎁',
    visitorMessage: {
      title: '🎁 Special Gift for You!',
      body: 'As our valued visitor, we\'d like to offer you a complimentary gift with your purchase. Thank you for visiting us!',
      emoji: '🎁',
      theme: {
        primaryColor: '#10B981',
        secondaryColor: '#ECFDF5',
        backgroundGradient: 'from-green-50 to-emerald-100',
        borderStyle: 'border-2 border-green-200 rounded-xl',
        iconStyle: 'bg-green-600 text-white'
      },
      actionButton: {
        text: 'Claim Gift',
        color: 'bg-green-600 hover:bg-green-700'
      }
    },
    offer: {
      discount: 'Free Gift',
      bonus: 'With Purchase',
      code: `GIFT${Date.now().toString().slice(-4)}`,
      validUntil: 'Dec 21, 2025'
    },
    preview: {
      style: 'bg-green-50 border-green-200',
      icon: '🎁',
      color: 'green'
    }
  }
};

const CampaignManagement: React.FC = () => {
  // State management
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [malls, setMalls] = useState<any[]>([]);
  // Initialize selectedMallId with authenticated mall ID from token
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  
  // Authentication state - Initialize with null, will be populated from token
  const [currentMallId, setCurrentMallId] = useState<number | null>(null);
  const [currentShopId, setCurrentShopId] = useState<number | null>(null);

  // QR Generation state
  const [showQRGeneration, setShowQRGeneration] = useState(false);
  const [selectedCampaignForQR, setSelectedCampaignForQR] = useState<any>(null);

  // WhatsApp integration state
  const [showNotification, setShowNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState('campaigns');

  // Token parsing function - Extracts user data from authentication token
  const parseAuthToken = (token: string): { mall_id: number; shop_id: number; user_id: string; username: string; role: string } | null => {
    console.log('🔍 PARSING TOKEN:', token);
    
    const parts = token.split('-');
    if (parts.length !== 6) {
      console.error('❌ Invalid token format. Expected 6 parts, got:', parts.length);
      return null;
    }
    
    const [user_id, username, role, mall_id, shop_id, timestamp] = parts;
    const parsedData = {
      user_id,
      username,
      role,
      mall_id: parseInt(mall_id),
      shop_id: parseInt(shop_id)
    };
    
    console.log('✅ TOKEN PARSED:', parsedData);
    return parsedData;
  };

  // Get user's mall and shop from token-based authentication
  const getCurrentUserMallAndShop = async () => {
    try {
      console.log('🔐 TOKEN-BASED AUTHENTICATION - Getting user mall and shop');
      
      const authToken = localStorage.getItem('geofence_auth_token');
      
      if (!authToken) {
        console.error('❌ No authenticated token found - user must be logged in');
        return { mall_id: null, shop_id: null };
      }

      const parsedToken = parseAuthToken(authToken);
      
      if (!parsedToken) {
        console.error('❌ Failed to parse token - authentication invalid');
        return { mall_id: null, shop_id: null };
      }

      console.log('🎯 AUTHENTICATED USER DATA:', {
        user_id: parsedToken.user_id,
        username: parsedToken.username,
        role: parsedToken.role,
        mall_id: parsedToken.mall_id,
        shop_id: parsedToken.shop_id
      });

      return {
        mall_id: parsedToken.mall_id,
        shop_id: parsedToken.shop_id
      };
      
    } catch (error) {
      console.error('❌ Error getting user mall and shop from token:', error);
      return { mall_id: null, shop_id: null };
    }
  };

  // Get current shop ID from token
  const getCurrentShopId = async (): Promise<number | null> => {
    try {
      console.log('🔍 Getting current shop ID from token');
      
      const authToken = localStorage.getItem('geofence_auth_token');
      
      if (!authToken) {
        console.error('❌ No auth token - user must be logged in');
        return null;
      }

      const parsedToken = parseAuthToken(authToken);
      
      if (!parsedToken || !parsedToken.shop_id) {
        console.error('❌ Failed to parse token or missing shop_id - authentication invalid');
        return null;
      }

      console.log('✅ Current shop ID from token:', parsedToken.shop_id);
      return parsedToken.shop_id;
      
    } catch (error) {
      console.error('❌ Error getting current shop ID:', error);
      return 6;
    }
  };

  // Fetch malls for dropdown
  const fetchMalls = async () => {
    try {
      console.log('🏢 Fetching available malls');
      
      const response = await fetch('https://n8n.tenear.com/webhook/management/malls', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('geofence_auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch malls: ${response.statusText}`);
      }

      const mallsData = await response.json();
      console.log('✅ Malls fetched:', mallsData.length);
      console.log('📊 Malls data received:', mallsData);
      
      // Handle both array and object response formats for malls
      let processedMalls;
      if (Array.isArray(mallsData)) {
        processedMalls = mallsData;
      } else if (mallsData && typeof mallsData === 'object' && Array.isArray(mallsData.data)) {
        processedMalls = mallsData.data;
      } else if (mallsData && typeof mallsData === 'object' && Array.isArray(mallsData.malls)) {
        processedMalls = mallsData.malls;
      } else {
        processedMalls = [];
        console.warn('⚠️ Unexpected malls data format:', mallsData);
      }
      
      setMalls(processedMalls);
      
      // Set default mall from token if not already set
      if (processedMalls.length > 0 && !selectedMallId) {
        const tokenData = await getCurrentUserMallAndShop();
        const defaultMall = processedMalls.find((mall: any) => mall.id === tokenData.mall_id);
        if (defaultMall) {
          setSelectedMallId(defaultMall.id);
          setCurrentMallId(defaultMall.id);
        }
      }
      
    } catch (error) {
      console.error('❌ Error fetching malls:', error);
      setError('Failed to fetch malls');
    }
  };

  // Helper function to determine campaign status with multiple fallbacks
  const getCampaignStatus = (campaign: any): boolean => {
    // Check various status fields in order of preference
    if (campaign.active !== undefined) return campaign.active;
    if (campaign.status) return campaign.status === 'active' || campaign.status === true;
    if (campaign.isActive !== undefined) return campaign.isActive;
    if (campaign.enabled !== undefined) return campaign.enabled;
    if (campaign.published !== undefined) return campaign.published;
    
    // Default to false if no status found
    return false;
  };

  // Helper function to format created date with multiple fallbacks
  // Track QR code scan via existing visitor-checkins webhook
  const trackQRScan = async (campaignId: string) => {
    try {
      console.log('📱 Tracking QR scan for campaign via visitor-checkins webhook:', campaignId);
      
      // Use the existing visitor-checkins webhook that the user mentioned
      const response = await fetch('https://n8n.tenear.com/webhook/visitor-checkins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'track_qr_scan',
          campaign_id: campaignId,
          scan_timestamp: new Date().toISOString(),
          scan_type: 'qr_code_scan'
        })
      });
      
      if (response.ok) {
        console.log('✅ QR scan tracked via visitor-checkins webhook');
        // Refresh campaigns to get updated scan counts
        if (selectedMallId !== null) {
          await fetchCampaignsForMall(selectedMallId);
        }
      } else {
        console.warn('⚠️ Failed to track QR scan via visitor-checkins webhook');
      }
    } catch (error) {
      console.error('❌ Error tracking QR scan:', error);
    }
  };

  // Enhanced WhatsApp template system - database templates disabled
  // The static template system provides 4 professional templates
  const fetchDatabaseTemplates = async () => {
    console.log("📋 Enhanced WhatsApp templates active - using static templates");
    return;
  };
  // Fetch database templates for current shop

  const formatCreatedDate = (campaign: any): string => {
    const possibleDateFields = [
      campaign.created_at,
      campaign.createdAt, 
      campaign.created?.at,
      campaign.created,
      campaign.timestamp,
      campaign.created_date
    ];
    
    for (const dateField of possibleDateFields) {
      if (dateField) {
        try {
          const date = new Date(dateField);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch (e) {
          console.warn('Invalid date format:', dateField);
        }
      }
    }
    
    return 'Unknown';
  };

  // FIELD MAPPING FIX: Handle current N8N response format
  const normalizeCampaignForDisplay = (campaign: any): any => {
    // Map N8N fields to frontend expected fields
    return {
      ...campaign,
      campaign_id: campaign.id || campaign.campaign_id,
      name: campaign.title || campaign.name,
      message: campaign.description || campaign.message,
      shop_name: campaign.locationName || campaign.shopName || campaign.shop_name,
      created_at: formatCreatedDate(campaign),
      shop_id: campaign.shopId || campaign.shop_id,
      mall_id: campaign.mallId || campaign.mall_id,
      active: campaign.isActive || campaign.active,
      scan_count: campaign.scan_count || campaign.scans || campaign.scanCount || 0
    };
  };

  // Fetch campaigns for selected mall
  const fetchCampaignsForMall = async (mallId: number) => {
    try {
      setLoading(true);
      console.log('📊 FETCHING CAMPAIGNS FOR MALL:', mallId);
      
      // Get authenticated shop ID from token
      const authenticatedShopId = await getCurrentShopId();
      console.log('🚨 CRITICAL DEBUG - Campaign fetch parameters:', { 
        authenticatedShopId, 
        mallId,
        fromToken: true 
      });

      const response = await fetch(`https://n8n.tenear.com/webhook/manage-campaigns-get?shop_id=${authenticatedShopId}&mall_id=${mallId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('geofence_auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Campaign fetch successful:', Array.isArray(data) ? data.length : 'non-array response', 'campaigns');
      console.log('📊 Campaign data received:', data);
      
      // DEBUG: Log first campaign structure
      if (Array.isArray(data) && data.length > 0) {
        console.log('🔍 First campaign structure sample:', JSON.stringify(data[0], null, 2));
      }
      
      // Handle both array and object response formats
      let campaignData;
      if (Array.isArray(data)) {
        // Direct array response
        campaignData = data;
      } else if (data && typeof data === 'object' && Array.isArray(data.campaigns)) {
        // Object with campaigns property
        campaignData = data.campaigns;
      } else {
        // Unknown format, default to empty array
        campaignData = [];
        console.warn('⚠️ Unexpected campaign data format:', data);
      }
      
      // APPLY FIELD MAPPING: Normalize campaign data for display
      const normalizedCampaignData = campaignData.map((campaign: any) => normalizeCampaignForDisplay(campaign));
      
      console.log('🎯 Extracted campaign data:', campaignData);
      console.log('🔍 First campaign structure sample:', campaignData[0] || 'No campaigns');
      if (campaignData[0]) {
        console.log('📋 Available fields in campaign:', Object.keys(campaignData[0]));
        console.log('🔄 Normalized campaign data:', normalizedCampaignData[0]);
      }
      setCampaigns(normalizedCampaignData);
      
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      
      // Check if this is an empty response error (n8n stopped workflow with no output)
      if (error instanceof SyntaxError && error.message.includes('Unexpected end of JSON input')) {
        // This typically happens when n8n returns an empty response because no campaigns were found
        // This is NOT an error - it's an expected empty state
        console.log('🎯 CampaignManagement: Empty response from n8n (no campaigns found), treating as empty state');
        setCampaigns([]);
        setError(null); // Clear any previous error
      } else if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        // Only set error for actual network issues
        setError('Failed to fetch campaigns. Please check your connection and try again.');
      } else {
        // For any other non-critical error, treat as empty state to avoid alarmist messages
        console.log('🎯 CampaignManagement: Non-critical error, setting empty campaigns instead of error state');
        setCampaigns([]);
        setError(null); // Don't show alarmist errors for expected empty states
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle mall change
  const handleMallChange = async (newMallId: number) => {
    try {
      console.log('🏢 HANDLING MALL CHANGE:', newMallId);
      
      // Get authenticated shop ID from token (for current mall)
      const authenticatedShopId = await getCurrentShopId();
      console.log('🔄 Using authenticated shop_id from token:', authenticatedShopId);
      
      setSelectedMallId(newMallId);
      setCurrentMallId(newMallId);
      
      // Fetch campaigns for the new mall
      await fetchCampaignsForMall(newMallId);
      
    } catch (error) {
      console.error('❌ Error handling mall change:', error);
      setError('Failed to switch mall');
    }
  };

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    try {
      console.log('📤 Submitting campaign form');
      
      // Get authenticated user and shop data from token
      const userData = await getCurrentUserMallAndShop();
      const authenticatedShopId = await getCurrentShopId();
      
      // Get user data from token
      const authToken = localStorage.getItem('geofence_auth_token');
      const parsedToken = authToken ? parseAuthToken(authToken) : null;
      
      // Get shop name from authenticated token or use shop ID as fallback
      const shopName = parsedToken?.username ? parsedToken.username : `Shop ${authenticatedShopId}`;
      
      // Determine action based on form mode
      const isUpdate = formMode === 'edit';
      const action = isUpdate ? 'update' : 'create';

      // Map form fields to N8N workflow expected format
      const campaignData = {
        action: action, // Required by N8N workflow for routing
        name: formData.title || '', // Campaign name
        description: formData.visitor_message || '', // Map visitor_message to description
        benefits: formData.offer_details || '', // Map offer_details to benefits
        discount_code: formData.discount_code || null,
        valid_until: formData.valid_until || null,
        active: formData.active !== undefined ? formData.active : true, // Include active status
        shop_id: authenticatedShopId?.toString() || '',
        mall_id: selectedMallId?.toString(),
        shop_name: shopName, // Use shop name from authenticated data
        user_id: parsedToken?.user_id?.toString() || null,
        username: parsedToken?.username || null,
        qr_codes: null, // QR codes will be integrated later with existing QR wizard system
        ...(isUpdate ? { id: formData.id } : {}), // Include ID for updates
        ...(isUpdate ? {} : { created_at: new Date().toISOString() }) // Only add created_at for new campaigns
      };

      console.log('🎯 FORM DATA WITH AUTHENTICATED SHOP_ID:', {
        ...campaignData,
        authenticatedShopId,
        currentShopId
      });

      const response = await fetch('https://n8n.tenear.com/webhook/manage-campaigns-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('geofence_auth_token')}`
        },
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        throw new Error(`Failed to submit campaign: ${response.statusText}`);
      }

      // Handle response more robustly - check if there's content to parse
      let result;
      try {
        const responseText = await response.text();
        console.log('📄 Raw response text:', responseText);
        
        if (responseText.trim() === '') {
          // Empty response - assume success
          console.log('✅ Empty response received, assuming success');
          result = { success: true, message: 'Campaign submitted successfully' };
        } else {
          // Try to parse as JSON
          result = JSON.parse(responseText);
          console.log('✅ Campaign submitted successfully:', result);
        }
      } catch (parseError) {
        // JSON parsing failed, assume success if we got a response
        console.log('✅ Non-JSON response received, assuming success:', parseError);
        result = { success: true, message: 'Campaign submitted successfully' };
      }
      
      // Reset form and refresh campaigns
      setShowForm(false);
      setFormMode('create');
      
      // Reset form state
      setSelectedCampaign(null);
      
      // Refresh campaigns list
      if (selectedMallId) {
        await fetchCampaignsForMall(selectedMallId);
      }
      
    } catch (error) {
      console.error('❌ Error submitting campaign:', error);
      setError('Failed to submit campaign');
    }
  };

  // Handle campaign deletion
  const handleDelete = async (campaignId: string) => {
    try {
      console.log('🗑️ Deleting campaign:', campaignId);
      
      if (!confirm('Are you sure you want to delete this campaign?')) {
        return;
      }

      const response = await fetch(`https://n8n.tenear.com/webhook/manage-campaigns-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('geofence_auth_token')}`
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          shop_id: (await getCurrentShopId())?.toString() || '',
          action: 'delete'  // Add action to distinguish from other POST requests
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to delete campaign: ${response.statusText}`);
      }

      console.log('✅ Campaign deleted successfully');
      
      // Refresh campaigns list
      if (selectedMallId) {
        await fetchCampaignsForMall(selectedMallId);
      }
      
    } catch (error) {
      console.error('❌ Error deleting campaign:', error);
      setError('Failed to delete campaign');
    }
  };

  // Reset form with authenticated shop_id
  const resetForm = () => {
    console.log('🔄 Resetting campaign form');
    
    // Get authenticated shop ID from token for form reset
    getCurrentShopId().then(authenticatedShopId => {
      console.log('🎯 FORM RESET - Using authenticated shop_id:', authenticatedShopId, 'instead of currentShopId:', currentShopId);
      
      setSelectedCampaign({
        title: '',
        description: '',
        template_id: 'welcome-offer',
        visitor_message: '',
        offer_details: '',
        discount_code: '',
        valid_until: '',
        shop_id: authenticatedShopId?.toString() || '', // Use authenticated shop_id from token
        mall_id: selectedMallId?.toString(),
        active: true
      });
    });
  };

  // Handle create campaign
  const handleCreateCampaign = () => {
    console.log('➕ Creating new campaign');
    setFormMode('create');
    resetForm();
    setShowForm(true);
  };

  // Handle edit campaign
  const handleViewCampaign = (campaign: any) => {
    console.log('👁️ Viewing campaign:', campaign.campaign_id || campaign.id);
    // For now, show alert with campaign details
    // TODO: Implement modal or detailed view later
    alert(`Campaign Details:\n\nName: ${campaign.name}\nDescription: ${campaign.message || campaign.description}\nStatus: ${campaign.active ? 'Active' : 'Inactive'}\nShop ID: ${campaign.shop_id}\nCreated: ${campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'Unknown'}`);
  };

  // Map N8N campaign data to edit form format
  const mapCampaignToEditForm = (campaign: any) => {
    console.log('🔄 Mapping campaign data for edit form:', campaign);
    const mapped = {
      ...campaign,
      id: campaign.campaign_id || campaign.id,  // Map N8N campaign_id → form id
      title: campaign.name,  // Map N8N name → form title
      visitor_message: campaign.message || campaign.description || campaign.visitor_message,  // Map N8N message → form visitor_message
      offer_details: campaign.benefits || campaign.offer_details,  // Map N8N benefits → form offer_details
      // Keep original fields for backward compatibility
      description: campaign.message || campaign.description || campaign.visitor_message || '',  // Map N8N message → description field
    };
    console.log('✅ Mapped campaign data:', mapped);
    return mapped;
  };

  const handleEditCampaign = (campaign: any) => {
    console.log('✏️ Editing campaign:', campaign.campaign_id || campaign.id);
    setFormMode('edit');
    const mappedCampaign = mapCampaignToEditForm(campaign);
    console.log('🎯 Setting selectedCampaign with mapped data:', mappedCampaign);
    setSelectedCampaign(mappedCampaign);
    setShowForm(true);
  };

  const handleGenerateQR = async (campaign: any) => {
    console.log('📱 Generating QR codes for campaign:', campaign.campaign_id || campaign.id);
    
    setSelectedCampaignForQR(campaign);
    setShowQRGeneration(true);
  };

  // Manual refresh scan counts
  const handleRefreshScans = async () => {
    console.log('🔄 Refreshing scan counts...');
    // FIX: Add null check to prevent TypeScript error
    if (selectedMallId !== null) {
      await fetchCampaignsForMall(selectedMallId);
    } else {
      console.warn('⚠️ No mall selected for refresh');
    }
  };

  const handleCloseQRGeneration = () => {
    setShowQRGeneration(false);
    setSelectedCampaignForQR(null);
  };

  // Enhanced WhatsApp message formatting functions
  const formatCampaignForWhatsApp = (campaign: any, templateType: 'promotional' | 'informative' | 'urgent' | 'exclusive' = 'promotional') => {
    // Extract campaign data with fallbacks
    const shopName = campaign.shop_name || campaign.shopName || `Shop ${campaign.shop_id || 'Unknown'}`;
    const mallName = malls.find(m => m.id === campaign.mall_id)?.name || 'Our Mall';
    const campaignName = campaign.name || 'Special Offer';
    const offerDetails = campaign.offer_details || campaign.benefits || 'Exclusive deals for you!';
    const discountCode = campaign.discount_code || 'Ask in store';
    const validUntil = campaign.valid_until || 'Limited time';
    const campaignDescription = campaign.description || campaign.message || '';
    const offerType = campaign.offer_type || 'Special Offer';
    const createdDate = new Date(campaign.created_at).toLocaleDateString() || 'Recently';
    
    // Template variations based on type
    const templates = {
      promotional: {
        header: `🔥 ${offerType} 🔥`,
        subheader: `🌟 ${campaignName} 🌟`,
        intro: `Don't miss out on this amazing offer from ${shopName} at ${mallName}!`,
        emoji: '🎉',
        cta: '💳 Use code: ' + discountCode,
        urgency: '',
        hashtags: `#${shopName.replace(/\s+/g, '')} #${mallName.replace(/\s+/g, '')} #Shopping #Deals`
      },
      informative: {
        header: `📢 Campaign Update`,
        subheader: `📋 ${campaignName}`,
        intro: `New campaign announcement from ${shopName} at ${mallName}`,
        emoji: 'ℹ️',
        cta: '💡 Details inside - Use code: ' + discountCode,
        urgency: '',
        hashtags: `#${shopName.replace(/\s+/g, '')} #${mallName.replace(/\s+/g, '')} #Update`
      },
      urgent: {
        header: `⚡ LIMITED TIME OFFER ⚡`,
        subheader: `🔥 ${campaignName} 🔥`,
        intro: `⏰ URGENT: This incredible offer from ${shopName} won't last long!`,
        emoji: '🚨',
        cta: `💰 Claim NOW - Code: ${discountCode}`,
        urgency: `⏳ Valid until: ${validUntil}`,
        hashtags: `#${shopName.replace(/\s+/g, '')} #LimitedTime #Urgent`
      },
      exclusive: {
        header: `💎 EXCLUSIVE OFFER 💎`,
        subheader: `✨ ${campaignName} ✨`,
        intro: `🎁 You're receiving this EXCLUSIVE offer from ${shopName} at ${mallName}!`,
        emoji: '👑',
        cta: `🔑 VIP Code: ${discountCode}`,
        urgency: `⏰ Available until: ${validUntil}`,
        hashtags: `#${shopName.replace(/\s+/g, '')} #Exclusive #VIP #Premium`
      }
    };
    
    const template = templates[templateType];
    
    // Build the complete message
    const message = `${template.header}

${template.subheader}

${template.intro}

📝 **OFFER DETAILS:**
${offerDetails}

${template.cta}

${template.urgency}

📅 Created: ${createdDate}
🏢 Location: ${mallName}

${campaignDescription ? `📄 ${campaignDescription}` : ''}

${template.cta.includes('Claim') ? '👆 Tap the link or show this message at our store!' : '💬 Visit us in-store for more details!'}

${template.hashtags}

---
💬 Share with friends who might be interested!`;

    return message;
  };

  const copyWhatsAppMessage = async (campaign: any, templateType: 'promotional' | 'informative' | 'urgent' | 'exclusive' = 'promotional') => {
    const message = formatCampaignForWhatsApp(campaign, templateType);
    
    try {
      // Check if clipboard API is available and secure context
      if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(message);
        
        setShowNotification({
          type: 'success',
          message: `WhatsApp message (${templateType}) copied to clipboard!`
        });
        
        setTimeout(() => setShowNotification(null), 3000);
        return;
      }
      
      throw new Error('Clipboard API not available or insecure context');
      
    } catch (error) {
      console.error('Failed to copy WhatsApp message:', error);
      
      // Enhanced fallback: Create a text area for manual selection
      const textArea = document.createElement('textarea');
      textArea.value = message;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setShowNotification({
          type: 'success',
          message: `WhatsApp message (${templateType}) copied successfully!`
        });
      } catch (fallbackError) {
        // Final fallback: Show in prompt
        prompt('Copy this WhatsApp message:', message);
        setShowNotification({
          type: 'error',
          message: 'Please copy the message from the prompt above.'
        });
      }
      
      // Clean up
      document.body.removeChild(textArea);
      setTimeout(() => setShowNotification(null), 5000);
    }
  };

  const shareToWhatsApp = (campaign: any, templateType: 'promotional' | 'informative' | 'urgent' | 'exclusive' = 'promotional') => {
    try {
      const message = formatCampaignForWhatsApp(campaign, templateType);
      const encodedMessage = encodeURIComponent(message);
      
      // Open WhatsApp with pre-filled message
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
      
      setShowNotification({
        type: 'success',
        message: `Opening WhatsApp with ${templateType} campaign message!`
      });
      setTimeout(() => setShowNotification(null), 3000);
      
    } catch (error) {
      console.error('Failed to share to WhatsApp:', error);
      setShowNotification({
        type: 'error',
        message: 'Failed to open WhatsApp. Please try again.'
      });
      setTimeout(() => setShowNotification(null), 3000);
    }
  };

  // Initialize component with token-based authentication
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        console.log('🚀 Initializing CampaignManagement with token-based authentication');
        
        // Get authenticated user mall and shop from token
        const userMallAndShop = await getCurrentUserMallAndShop();
        const mallId = userMallAndShop.mall_id;
        const shopId = userMallAndShop.shop_id;
        
        console.log('🔐 INITIALIZATION - Authenticated from token:', {
          mall_id: mallId,
          shop_id: shopId,
          timestamp: new Date().toISOString()
        });
        
        // Set state with authenticated values
        setCurrentMallId(mallId);
        setCurrentShopId(shopId);
        setSelectedMallId(mallId);
        
        // Fetch malls and campaigns
        await fetchMalls();
        
        console.log('✅ CampaignManagement initialization complete');
        
      } catch (error) {
        console.error('❌ Error initializing component:', error);
        setError('Failed to initialize component');
      }
    };

    initializeComponent();
  }, []);

  // Fetch campaigns when mall changes
  useEffect(() => {
    if (selectedMallId) {
      console.log('🔄 Fetching campaigns for mall:', selectedMallId);
      fetchCampaignsForMall(selectedMallId);
    }
  }, [selectedMallId]);

  // Enhanced WhatsApp templates are used via buttons in campaign list

  // Get current user data for notifications
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userData = await getCurrentUserMallAndShop();
        const authToken = localStorage.getItem('geofence_auth_token');
        const parsedToken = authToken ? parseAuthToken(authToken) : null;
        
        if (parsedToken) {
          // Use the mall_id and shop_id from the token directly
          // Don't override with potentially null values from getCurrentUserMallAndShop
          setCurrentUser({
            ...parsedToken,
            mall_id: parsedToken.mall_id,  // From token - more reliable
            shop_id: parsedToken.shop_id   // From token - more reliable
          });
        }
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };
    
    loadCurrentUser();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with Notifications */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaign Management</h1>
            <p className="text-gray-600">Manage promotional campaigns for your mall and shops</p>
          </div>
          {/* Real-time Notification Center */}
          {currentUser && (
            <NotificationCenter user={currentUser} />
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* WhatsApp Integration Notifications */}
      {showNotification && (
        <div className={`${
          showNotification.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-700' 
            : 'bg-red-50 border-red-200 text-red-700'
        } border px-4 py-3 rounded mb-4`}>
          {showNotification.message}
        </div>
      )}

      {/* Mall Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Select Mall:</label>
          <select
            value={selectedMallId || ''}
            onChange={(e) => handleMallChange(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a mall</option>
            {malls.map(mall => (
              <option key={mall.id} value={mall.id}>
                {mall.name}
              </option>
            ))}
          </select>
          
          {selectedMallId && (
            <div className="text-sm text-gray-600">
              Current Mall: {malls.find(m => m.id === selectedMallId)?.name || `Mall ID ${selectedMallId}`}
            </div>
          )}
        </div>
      </div>

      {selectedMallId && (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Campaigns</h3>
                <p className="text-3xl font-bold text-blue-600">{Array.isArray(campaigns) ? campaigns.length : 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Campaigns</h3>
                <p className="text-3xl font-bold text-green-600">
                  {Array.isArray(campaigns) ? campaigns.filter(c => c.active).length : 0}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Mall ID</h3>
                <p className="text-3xl font-bold text-purple-600">{selectedMallId}</p>
              </div>
            </div>

            {/* Tab Navigation */}
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="campaigns" className="flex items-center">
                <span className="mr-2">📢</span>
                Campaign Management
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="flex items-center">
                <span className="mr-2">📱</span>
                WhatsApp Templates
              </TabsTrigger>
            </TabsList>

            {/* Campaign Management Tab */}
            <TabsContent value="campaigns">
              {/* Action Buttons */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Campaigns</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleRefreshScans}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md flex items-center space-x-2 text-sm"
                  >
                    <span>🔄</span>
                    <span>Refresh Scans</span>
                  </button>
                  <button
                    onClick={handleCreateCampaign}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                  >
                    <span>➕</span>
                    <span>Create Campaign</span>
                  </button>
                </div>
              </div>
              {/* Campaign Display */}
              {loading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <div className="text-gray-500">Loading campaigns...</div>
                </div>
              ) : (Array.isArray(campaigns) && campaigns.length === 0) ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <div className="text-gray-500">No campaigns found. Create your first campaign!</div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.isArray(campaigns) && campaigns.map(campaign => (
                  <div key={campaign.campaign_id || campaign.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        getCampaignStatus(campaign) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {getCampaignStatus(campaign) ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3">{campaign.message || campaign.description || 'No description'}</p>
                    
                    <div className="text-xs text-gray-500 mb-3">
                      <div>Shop: {campaign.shop_name || campaign.shopName || campaign.shop?.name || campaign.shop?.name || `Shop ${campaign.shop_id || campaign.shopId || campaign.shop?.id || 'Unknown'}`}</div>
                      <div>Created: {formatCreatedDate(campaign)}</div>
                      <div>Scans: {normalizeCampaignForDisplay(campaign).scan_count} total</div>
                      <div>ID: {normalizeCampaignForDisplay(campaign).campaign_id || 'N/A'}</div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => handleViewCampaign(campaign)}
                        className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                      >
                        <span>👁️</span>
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => handleEditCampaign(campaign)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                      >
                        <span>✏️</span>
                        <span>Edit</span>
                      </button>
                      {/* Enhanced WhatsApp Template Selection */}
                      <div className="relative group">
                        <button
                          onClick={() => copyWhatsAppMessage(campaign, 'promotional')}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                        >
                          <span>📋</span>
                          <span>Copy</span>
                        </button>
                        <button
                          onClick={() => shareToWhatsApp(campaign, 'promotional')}
                          className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                        >
                          <span>📲</span>
                          <span>WhatsApp</span>
                        </button>
                        {/* Template Selection Dropdown */}
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 min-w-48">
                          <div className="p-2 border-b border-gray-100">
                            <div className="text-xs font-semibold text-gray-700 mb-1">Message Templates</div>
                            <div className="text-xs text-gray-500">Choose template style</div>
                          </div>
                          <div className="p-1">
                            <button
                              onClick={() => copyWhatsAppMessage(campaign, 'promotional')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 rounded flex items-center space-x-2"
                            >
                              <span>🔥</span>
                              <div>
                                <div className="font-medium">Promotional</div>
                                <div className="text-gray-500">Exciting deals & offers</div>
                              </div>
                            </button>
                            <button
                              onClick={() => copyWhatsAppMessage(campaign, 'urgent')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 rounded flex items-center space-x-2"
                            >
                              <span>⚡</span>
                              <div>
                                <div className="font-medium">Urgent</div>
                                <div className="text-gray-500">Limited time offers</div>
                              </div>
                            </button>
                            <button
                              onClick={() => copyWhatsAppMessage(campaign, 'exclusive')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 rounded flex items-center space-x-2"
                            >
                              <span>👑</span>
                              <div>
                                <div className="font-medium">Exclusive</div>
                                <div className="text-gray-500">VIP & premium offers</div>
                              </div>
                            </button>
                            <button
                              onClick={() => copyWhatsAppMessage(campaign, 'informative')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 rounded flex items-center space-x-2"
                            >
                              <span>ℹ️</span>
                              <div>
                                <div className="font-medium">Informative</div>
                                <div className="text-gray-500">News & announcements</div>
                              </div>
                            </button>
                          </div>
                          <div className="p-2 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-700 mb-1">WhatsApp Direct</div>
                            <button
                              onClick={() => shareToWhatsApp(campaign, 'promotional')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 rounded flex items-center space-x-2"
                            >
                              <span>📲</span>
                              <span>Send with promotional template</span>
                            </button>
                            <button
                              onClick={() => shareToWhatsApp(campaign, 'urgent')}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 rounded flex items-center space-x-2"
                            >
                              <span>⚡</span>
                              <span>Send with urgent template</span>
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => handleGenerateQR(campaign)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                        >
                          <span>📱</span>
                          <span>QR</span>
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.campaign_id || campaign.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs flex items-center space-x-1"
                        >
                          <span>🗑️</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </TabsContent>

            {/* WhatsApp Templates Tab Content */}
            <TabsContent value="whatsapp" className="mt-6">
              <WhatsAppTemplateManager />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Campaign Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {formMode === 'create' ? 'Create Campaign' : 'Edit Campaign'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

              {/* Campaign Templates - Using Enhanced WhatsApp System */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">WhatsApp Message Templates</h3>
                    <p className="text-xs text-gray-500">Enhanced template system built into WhatsApp integration</p>
                  </div>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded text-xs">
                    ✅ Enhanced Templates Active
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-green-600">🎯</span>
                    <span className="text-sm font-medium text-green-800">4 Template Styles Available</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center space-x-1">
                      <span>🔥</span>
                      <span>Promotional</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span>⚡</span>
                      <span>Urgent</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span>👑</span>
                      <span>Exclusive</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span>ℹ️</span>
                      <span>Informative</span>
                    </div>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    Access these templates by hovering over WhatsApp buttons in the campaign list above.
                  </p>
                </div>
              </div>

              {/* Rest of the form continues... */}
              <div className="space-y-4">

              <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(selectedCampaign);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campaign Title *
                    </label>
                    <input
                      type="text"
                      value={selectedCampaign?.title || ''}
                      onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      value={selectedCampaign?.description || ''}
                      onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Visitor Message *
                    </label>
                    <textarea
                      value={selectedCampaign?.visitor_message || ''}
                      onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, visitor_message: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      placeholder="Message that visitors will receive..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer Details *
                    </label>
                    <input
                      type="text"
                      value={selectedCampaign?.offer_details || ''}
                      onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, offer_details: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 20% OFF - Free gift with purchase"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Code
                      </label>
                      <input
                        type="text"
                        value={selectedCampaign?.discount_code || ''}
                        onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, discount_code: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., WELCOME2025"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valid Until
                      </label>
                      <input
                        type="text"
                        value={selectedCampaign?.valid_until || ''}
                        onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, valid_until: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Dec 31, 2025"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="active"
                      checked={selectedCampaign?.active ?? true}
                      onChange={(e) => setSelectedCampaign((prev: any) => ({ ...prev, active: e.target.checked }))}
                      className="mr-2"
                    />
                    <label htmlFor="active" className="text-sm text-gray-700">
                      Campaign is active
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    >
                      {formMode === 'create' ? 'Create Campaign' : 'Update Campaign'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

          {/* QR Generation Modal */}
      {showQRGeneration && selectedCampaignForQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Generate QR Codes for: {selectedCampaignForQR.name}
              </h2>
              <button
                onClick={handleCloseQRGeneration}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              <QRGeneration 
                preselectedCampaign={selectedCampaignForQR}
                onClose={handleCloseQRGeneration}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;
